import {
  CustomInstrumentRequestStatusEnum,
  CustomInstrumentRequestTypeEnum,
  FetchError,
  OptionTypeEnum,
  ProductTypeEnum,
  SettleValueTypeEnum,
  getFetchErrorUserMessage,
  prettyName,
  toBig,
  type ICustomInstrumentRequestResponse,
  type Security,
  type SubscriptionResponse,
} from '@talos/kyoko';
import { omitBy } from 'lodash-es';
import {
  CopyFromListedSymbol,
  CustomSecurityMarket,
  type CreateCustomInstrumentRequest,
  type CreateCustomSecurityForm,
  type CustomInstrumentCreateResult,
} from './customSecurityWorkflowTypes';

export const CUSTOM_SECURITY_PRODUCT_TYPES = [
  ProductTypeEnum.CFD,
  ProductTypeEnum.PerpetualSwap,
  ProductTypeEnum.Option,
] as const;

export type CustomSecurityProductType = (typeof CUSTOM_SECURITY_PRODUCT_TYPES)[number];

// Product types supported by this workflow that require an expiration date.
export const PRODUCT_TYPES_WITH_EXPIRY: ProductTypeEnum[] = [ProductTypeEnum.Option];

export const DEFAULT_CREATE_CUSTOM_SECURITY_FORM: CreateCustomSecurityForm = {
  CopyFromListedSymbol: CopyFromListedSymbol.Yes,
  NotionalSize: '1',
  ContractSize: '1',
  SettleValueType: SettleValueTypeEnum.Regular,
};

export function isCustomSecurityProductType(
  productType: ProductTypeEnum | undefined
): productType is CustomSecurityProductType {
  return CUSTOM_SECURITY_PRODUCT_TYPES.includes(productType as CustomSecurityProductType);
}

export function getProductTypeOptions() {
  return CUSTOM_SECURITY_PRODUCT_TYPES.map(productType => ({
    value: productType,
    label: getProductTypeLabel(productType),
    description: getProductTypeDescription(productType),
  }));
}

export function getProductTypeLabel(productType: ProductTypeEnum): string {
  if (productType === ProductTypeEnum.CFD) {
    return productType;
  }
  return prettyName(productType);
}

function getProductTypeDescription(productType: ProductTypeEnum): string {
  switch (productType) {
    case ProductTypeEnum.CFD:
      return 'Contract for difference custom security.';
    case ProductTypeEnum.PerpetualSwap:
      return 'Perpetual Swap custom security without expiry.';
    case ProductTypeEnum.Option:
      return 'Option custom security with strike, expiry, and option type.';
    default:
      return '';
  }
}

export function productTypeRequiresExpiry(productType: ProductTypeEnum | undefined): boolean {
  return productType != null && PRODUCT_TYPES_WITH_EXPIRY.includes(productType);
}

// Custom instruments only support Regular settlement.
export function getSettleValueTypeOptions() {
  return [{ value: SettleValueTypeEnum.Regular, label: 'Regular' }];
}

/**
 * Whether ContractSize is greater than 1. Used to enforce that PositionCurrency must be empty
 * when the contract is a multi-unit derivative (per backend constraint).
 */
export function isContractSizeGreaterThanOne(contractSize: string | undefined): boolean {
  const big = toBig(contractSize);
  return big != null && big.gt(1);
}

/**
 * Securities are valid copy sources only when they use Regular settlement and are not
 * self-settling. Securities whose base currency equals settlement currency are always
 * filtered out, regardless of settle type.
 *
 * Securities without a SettleValueType are treated as Regular — in production data they
 * always have one, but test fixtures or older records may omit it.
 */
export function isCopyableSourceSecurity(security: Security): boolean {
  if (security.BaseCurrency === security.SettlementCurrency) {
    return false;
  }
  if (!security.SettleValueType || security.SettleValueType === SettleValueTypeEnum.Regular) {
    return true;
  }
  return false;
}

export function getMarketOptions() {
  return [
    { value: CustomSecurityMarket.Internal, label: CustomSecurityMarket.Internal },
    { value: CustomSecurityMarket.InternalWhitelabel, label: CustomSecurityMarket.InternalWhitelabel },
  ];
}

export function getOptionTypeOptions() {
  return [
    { value: OptionTypeEnum.Call, label: 'Call', description: 'Right to buy the underlying at the strike price.' },
    { value: OptionTypeEnum.Put, label: 'Put', description: 'Right to sell the underlying at the strike price.' },
  ];
}

export function getCopyFromListedSymbolOptions() {
  return [
    { displayName: 'Yes', value: CopyFromListedSymbol.Yes },
    { displayName: 'No', value: CopyFromListedSymbol.No },
  ];
}

export function getCopyableFieldsFromSecurity(security: Security): Partial<CreateCustomSecurityForm> {
  // ContractSize on the form maps to NotionalMultiplier on the source security.
  // Default to '1' when the source has no notional multiplier.
  const contractSize = security.NotionalMultiplier?.trim() ? security.NotionalMultiplier : '1';
  // Per backend constraint: if ContractSize > 1, PositionCurrency must be empty.
  const positionCurrency = isContractSizeGreaterThanOne(contractSize) ? undefined : security.PositionCurrency;
  return {
    Description: security.Description,
    Market: getCopyableMarket(security.Markets),
    QuoteCurrency: security.QuoteCurrency,
    PositionCurrency: positionCurrency,
    DefaultSizeIncrement: security.DefaultSizeIncrement,
    MinSizeIncrement: security.MinSizeIncrement,
    ContractSize: contractSize,
    MinPriceIncrement: security.MinPriceIncrement,
    DefaultPriceIncrement: security.DefaultPriceIncrement,
    SettlementCurrency: security.SettlementCurrency,
    SettleValueType: SettleValueTypeEnum.Regular,
    BaseCurrency: security.BaseCurrency,
    PriceDisplaySpec: security.PriceDisplaySpec,
    SizeDisplaySpec: security.SizeDisplaySpec,
    UnderlyingQuoteCurrency: security.UnderlyingQuoteCurrency,
    NotionalSize: '1',
    StrikePrice: security.StrikePrice,
    OptionType: security.OptionType,
    // Pre-fill Symbol/DisplaySymbol so the user has a starting point; uniqueness is
    // enforced by validateSymbol (so the user is forced to change it). The " - Duplicate"
    // suffix on DisplaySymbol makes it obvious the value needs to be updated.
    Symbol: security.Symbol,
    DisplaySymbol: `${security.DisplaySymbol || security.Symbol} - Duplicate`,
    Expiry: parseSecurityExpiration(security.Expiration),
  };
}

export function getFormWithCopiedSecurityFields(
  form: CreateCustomSecurityForm,
  security: Security
): CreateCustomSecurityForm {
  return {
    ...form,
    ...getCopyableFieldsFromSecurity(security),
    CopySourceSymbol: security.Symbol,
  };
}

export function getFormWithoutCopiedSecurityFields(form: CreateCustomSecurityForm): CreateCustomSecurityForm {
  return {
    ...DEFAULT_CREATE_CUSTOM_SECURITY_FORM,
    ProductType: form.ProductType,
    CopyFromListedSymbol: CopyFromListedSymbol.No,
  };
}

export function validateSymbol(
  form: CreateCustomSecurityForm,
  securitiesBySymbol: Map<string, Security> | undefined
): string | undefined {
  const rawSymbol = form.Symbol ?? '';
  const symbol = rawSymbol.trim();
  if (!symbol) {
    return 'This field is required';
  }
  if (/\s/.test(rawSymbol)) {
    return 'Symbol cannot contain spaces';
  }
  if (!/^[A-Za-z0-9:_-]+$/.test(rawSymbol)) {
    return 'Symbol can only contain letters, numbers, dash, colon, and underscore';
  }
  if (symbol.length > 64) {
    return 'Symbol must be 64 characters or fewer';
  }
  if (securitiesBySymbol?.has(symbol)) {
    return 'A security with this symbol already exists';
  }
  return undefined;
}

export function validateDisplaySymbol(
  form: CreateCustomSecurityForm,
  securitiesBySymbol: Map<string, Security> | undefined,
  securitiesByDisplaySymbol: Map<string, Security> | undefined
): string | undefined {
  const displaySymbol = form.DisplaySymbol?.trim();
  if (!displaySymbol) {
    return 'This field is required';
  }
  if (displaySymbol.length > 64) {
    return 'Display symbol must be 64 characters or fewer';
  }
  if (securitiesBySymbol?.has(displaySymbol)) {
    return 'Display symbol must not match an existing symbol';
  }
  if (securitiesByDisplaySymbol?.has(displaySymbol)) {
    return 'A security with this display symbol already exists';
  }
  return undefined;
}

export function validatePositiveInteger(value: string | undefined): string | undefined {
  if (!value?.trim()) {
    return 'This field is required';
  }
  if (!/^[1-9]\d*$/.test(value.trim())) {
    return 'Strike price must be a positive integer';
  }
  return undefined;
}

export function buildCreateCustomInstrumentRequest(form: CreateCustomSecurityForm): CreateCustomInstrumentRequest {
  if (!isCustomSecurityProductType(form.ProductType)) {
    throw new Error('Select a product type');
  }
  const symbol = form.Symbol?.trim();
  if (!symbol) {
    throw new Error('Symbol is required');
  }

  const {
    CopyFromListedSymbol: _copy,
    CopySourceSymbol: _source,
    Expiry,
    NotionalSize: _notional,
    UnderlyingQuoteCurrency: _uqc,
    ...formFields
  } = form;

  const request: CreateCustomInstrumentRequest = omitBy(
    {
      ...formFields,
      RequestType:
        form.ProductType === ProductTypeEnum.Option
          ? CustomInstrumentRequestTypeEnum.Option
          : CustomInstrumentRequestTypeEnum.Generic,
      Symbol: symbol,
      ProductDetails: getProductDetails(form),
    },
    v => v === undefined || v === null || v === ''
  ) as CreateCustomInstrumentRequest;

  if (!productTypeRequiresExpiry(form.ProductType)) {
    return request;
  }

  if (!Expiry) {
    throw new Error(`Expiry is required for ${getProductTypeLabel(form.ProductType)}`);
  }

  return omitBy(
    {
      ...request,
      Expiration: formatSecurityExpiration(Expiry),
    },
    v => v === undefined || v === null || v === ''
  ) as CreateCustomInstrumentRequest;
}

function getProductDetails(form: CreateCustomSecurityForm): CreateCustomInstrumentRequest['ProductDetails'] {
  return omitBy(
    {
      NotionalSize: form.NotionalSize || '1',
      UnderlyingQuoteCurrency:
        form.ProductType === ProductTypeEnum.Option ? form.UnderlyingQuoteCurrency || form.QuoteCurrency : undefined,
    },
    v => v === undefined || v === null || v === ''
  ) as CreateCustomInstrumentRequest['ProductDetails'];
}

export function parseCustomInstrumentCreateResult(
  response: SubscriptionResponse<ICustomInstrumentRequestResponse, 'CustomInstrument'>,
  productType: ProductTypeEnum
): CustomInstrumentCreateResult {
  const responseData = response.data[0];
  if (!responseData) {
    throw new Error('No custom instrument response was returned');
  }

  if (
    responseData.Status !== CustomInstrumentRequestStatusEnum.Created &&
    responseData.Status !== CustomInstrumentRequestStatusEnum.LookedUp
  ) {
    throw new Error(responseData.Text || `Custom instrument request returned ${responseData.Status}`);
  }

  return {
    status: responseData.Status,
    timestamp: responseData.Timestamp,
    symbol: responseData.Symbol,
    productType,
  };
}

export function getCustomInstrumentErrorMessage(error: unknown): string {
  if (error instanceof FetchError) {
    return getFetchErrorUserMessage(error, 'Unable to create custom security');
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Unable to create custom security';
}

function getCopyableMarket(markets: string[] | undefined): CustomSecurityMarket | undefined {
  if (markets?.includes(CustomSecurityMarket.Internal)) {
    return CustomSecurityMarket.Internal;
  }
  if (markets?.includes(CustomSecurityMarket.InternalWhitelabel)) {
    return CustomSecurityMarket.InternalWhitelabel;
  }
  return undefined;
}

/**
 * Security.Expiration is a `JSON_UTCTimestamp` (ISO date string), not a nanosecond bigint.
 */
function parseSecurityExpiration(timestamp: string | undefined): Date | undefined {
  if (!timestamp) {
    return undefined;
  }
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function formatSecurityExpiration(expiry: Date): string {
  return expiry.toISOString();
}
